import { useEffect, useState, useMemo } from 'react';
import financasApi from '@/lib/financasApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, ShoppingCart, User, Wrench, Package, Search, Pencil, X, UserPlus, Check } from 'lucide-react';
import { toast } from 'sonner';

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '-';
const FORMAS = ['Dinheiro', 'PIX', 'Cartão Débito', 'Cartão Crédito', 'Boleto', 'Transferência'];
const STATUS_LABEL = { pago: 'Pago', pendente: 'Pendente', atrasado: 'Atrasado' };
const STATUS_COLOR = {
  pago: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  pendente: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  atrasado: 'bg-red-500/20 text-red-300 border-red-500/30',
};

const itemVazio = () => ({ tipo: 'servico', descricao: '', quantidade: 1, valor_unitario: '', valor_total: 0 });

export default function FinancasVendas() {
  const [vendas, setVendas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [busca, setBusca] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showNovoCliente, setShowNovoCliente] = useState(false);
  const [novoClienteNome, setNovoClienteNome] = useState('');
  const [novoClienteFone, setNovoClienteFone] = useState('');
  const [buscaCliente, setBuscaCliente] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [form, setForm] = useState({
    cliente_id: '',
    cliente_nome: '',
    items: [itemVazio()],
    desconto: 0,
    forma_pagamento: 'PIX',
    status: 'pago',
    data: new Date().toISOString().slice(0, 10),
    data_vencimento: '',
    observacao: '',
  });

  const load = async () => {
    const { data } = await financasApi.get('/financas/vendas');
    setVendas(data);
  };
  const loadClientes = async () => {
    const { data } = await financasApi.get('/financas/clientes');
    setClientes(data);
  };
  useEffect(() => { load(); loadClientes(); }, []);

  const filtered = useMemo(() => {
    let arr = vendas;
    if (statusFilter !== 'todos') arr = arr.filter(v => v.status === statusFilter);
    if (busca) {
      const b = busca.toLowerCase();
      arr = arr.filter(v =>
        (v.cliente_nome || '').toLowerCase().includes(b) ||
        (v.observacao || '').toLowerCase().includes(b) ||
        (v.items || []).some(it => (it.descricao || '').toLowerCase().includes(b))
      );
    }
    return arr;
  }, [vendas, statusFilter, busca]);

  const totais = useMemo(() => {
    return filtered.reduce((acc, v) => {
      acc.total += v.valor_total || 0;
      if (v.status === 'pago') acc.pago += v.valor_total || 0;
      else acc.pendente += v.valor_total || 0;
      return acc;
    }, { total: 0, pago: 0, pendente: 0 });
  }, [filtered]);

  const subtotal = useMemo(() => form.items.reduce((s, it) => s + (Number(it.valor_total) || 0), 0), [form.items]);
  const totalForm = useMemo(() => Math.max(0, subtotal - (Number(form.desconto) || 0)), [subtotal, form.desconto]);

  const openNova = () => {
    setEditing(null);
    setForm({
      cliente_id: '', cliente_nome: '', items: [itemVazio()],
      desconto: 0, forma_pagamento: 'PIX', status: 'pago',
      data: new Date().toISOString().slice(0, 10), data_vencimento: '', observacao: '',
    });
    setOpen(true);
  };

  const openEditar = (v) => {
    setEditing(v);
    setForm({
      cliente_id: v.cliente_id || '',
      cliente_nome: v.cliente_id ? '' : v.cliente_nome || '',
      items: v.items.map(it => ({ ...it, valor_unitario: String(it.valor_unitario), valor_total: it.valor_total })),
      desconto: v.desconto || 0,
      forma_pagamento: v.forma_pagamento || 'PIX',
      status: v.status || 'pago',
      data: v.data || new Date().toISOString().slice(0, 10),
      data_vencimento: v.data_vencimento || '',
      observacao: v.observacao || '',
    });
    setOpen(true);
  };

  const addItem = () => setForm({ ...form, items: [...form.items, itemVazio()] });
  const removeItem = (i) => {
    if (form.items.length === 1) return;
    setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  };
  const updateItem = (i, patch) => {
    const novos = form.items.map((it, idx) => {
      if (idx !== i) return it;
      const merged = { ...it, ...patch };
      const qty = Number(merged.quantidade) || 0;
      const vu = Number(merged.valor_unitario) || 0;
      merged.valor_total = +(qty * vu).toFixed(2);
      return merged;
    });
    setForm({ ...form, items: novos });
  };

  const criarClienteRapido = async () => {
    if (!novoClienteNome.trim()) { toast.error('Nome obrigatório'); return; }
    try {
      const { data } = await financasApi.post('/financas/clientes', { nome: novoClienteNome.trim(), telefone: novoClienteFone || null });
      toast.success('Cliente criado');
      setNovoClienteNome(''); setNovoClienteFone(''); setShowNovoCliente(false);
      await loadClientes();
      setForm({ ...form, cliente_id: data.id, cliente_nome: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const items = form.items.filter(it => it.descricao.trim() && Number(it.valor_unitario) > 0);
    if (items.length === 0) { toast.error('Adicione ao menos 1 item válido'); return; }
    if (!form.cliente_id && !form.cliente_nome.trim()) { toast.error('Selecione ou informe um cliente'); return; }
    const payload = {
      cliente_id: form.cliente_id || null,
      cliente_nome: form.cliente_id ? null : form.cliente_nome.trim(),
      items: items.map(it => ({
        tipo: it.tipo,
        descricao: it.descricao.trim(),
        quantidade: Number(it.quantidade),
        valor_unitario: Number(it.valor_unitario),
        valor_total: Number(it.valor_total),
      })),
      desconto: Number(form.desconto) || 0,
      forma_pagamento: form.forma_pagamento,
      status: form.status,
      data: form.data,
      data_vencimento: form.data_vencimento || null,
      observacao: form.observacao || null,
    };
    try {
      if (editing) {
        await financasApi.put(`/financas/vendas/${editing.id}`, payload);
        toast.success('Venda atualizada');
      } else {
        await financasApi.post('/financas/vendas', payload);
        toast.success('Venda registrada! Lançamento criado em Entradas.');
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const excluir = async (v) => {
    if (!window.confirm(`Excluir venda para ${v.cliente_nome}? A entrada vinculada também será removida.`)) return;
    await financasApi.delete(`/financas/vendas/${v.id}`);
    toast.success('Venda removida');
    load();
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="financas-vendas-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3"><ShoppingCart className="w-6 h-6 md:w-7 md:h-7 text-emerald-400" /> Vendas / OS</h1>
          <p className="text-slate-400 text-xs md:text-sm mt-1">Vendas de serviços ou mercadorias. Entrada lançada automaticamente.</p>
        </div>
        <Button onClick={openNova} data-testid="financas-venda-nova-btn" className="bg-emerald-600 hover:bg-emerald-500 text-white">
          <Plus className="w-4 h-4 mr-2" /> Nova Venda
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6">
        <div className="bg-slate-900 border border-white/10 rounded-xl p-3 md:p-4">
          <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider">Total</p>
          <p className="text-lg md:text-2xl font-bold text-emerald-400 mt-1 truncate">{fmtBRL(totais.total)}</p>
        </div>
        <div className="bg-slate-900 border border-white/10 rounded-xl p-3 md:p-4">
          <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider">Recebido</p>
          <p className="text-lg md:text-2xl font-bold text-emerald-400 mt-1 truncate">{fmtBRL(totais.pago)}</p>
        </div>
        <div className="bg-slate-900 border border-white/10 rounded-xl p-3 md:p-4">
          <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider">A receber</p>
          <p className="text-lg md:text-2xl font-bold text-amber-400 mt-1 truncate">{fmtBRL(totais.pendente)}</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input data-testid="financas-vendas-busca" placeholder="Buscar por cliente, item ou observação..." value={busca} onChange={(e) => setBusca(e.target.value)} className="bg-slate-900 border-white/10 text-white pl-10" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-slate-900 border-white/10 text-white w-full sm:w-44"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-white/10 text-white">
            <SelectItem value="todos">Todos status</SelectItem>
            <SelectItem value="pago">Pago</SelectItem>
            <SelectItem value="pendente">Pendente</SelectItem>
            <SelectItem value="atrasado">Atrasado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Cards mobile */}
      <div className="md:hidden space-y-2">
        {filtered.length === 0 ? (
          <div className="bg-slate-900 border border-white/10 rounded-xl py-12 text-center text-slate-500">Nenhuma venda registrada</div>
        ) : filtered.map(v => (
          <div key={v.id} className="bg-slate-900 border border-white/10 rounded-xl p-3 active:bg-white/5">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-white text-sm font-medium">
                  <User className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                  <span className="truncate">{v.cliente_nome}</span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{fmtDate(v.data)} · {v.forma_pagamento}</p>
                <p className="text-xs text-slate-500 truncate mt-0.5">{(v.items || []).map(it => it.descricao).join(', ')}</p>
              </div>
              <p className="font-semibold text-emerald-400 whitespace-nowrap text-sm">{fmtBRL(v.valor_total)}</p>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-white/5">
              <span className={`px-2 py-0.5 rounded-full border text-[10px] ${STATUS_COLOR[v.status]}`}>{STATUS_LABEL[v.status]}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEditar(v)} className="text-slate-400 h-7 w-7 p-0"><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => excluir(v)} className="text-red-500 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabela desktop */}
      <div className="hidden md:block bg-slate-900 border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/50">
            <tr className="text-left text-slate-400 text-xs uppercase tracking-wider">
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Itens</th>
              <th className="px-4 py-3">Pagamento</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">Nenhuma venda registrada</td></tr>
            ) : filtered.map(v => (
              <tr key={v.id} className="hover:bg-white/5">
                <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(v.data)}</td>
                <td className="px-4 py-3 text-white">
                  <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-slate-500" />{v.cliente_nome}</div>
                </td>
                <td className="px-4 py-3 text-slate-400">
                  <div className="text-xs">{(v.items || []).length} {(v.items || []).length === 1 ? 'item' : 'itens'}</div>
                  <div className="text-xs text-slate-500 truncate max-w-[200px]">{(v.items || []).map(it => it.descricao).join(', ')}</div>
                </td>
                <td className="px-4 py-3 text-slate-400">{v.forma_pagamento}</td>
                <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full border text-xs ${STATUS_COLOR[v.status]}`}>{STATUS_LABEL[v.status]}</span></td>
                <td className="px-4 py-3 text-right font-semibold text-emerald-400">{fmtBRL(v.valor_total)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEditar(v)} className="text-slate-400 hover:text-white" data-testid={`financas-venda-editar-${v.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => excluir(v)} className="text-red-500 hover:text-red-400" data-testid={`financas-venda-excluir-${v.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog Nova/Editar Venda */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-3xl w-[96vw] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Venda' : 'Nova Venda / Ordem de Serviço'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Adicione itens (serviço ou mercadoria) e selecione o cliente. A entrada é lançada automaticamente.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {/* Cliente */}
            <div className="bg-slate-950 border border-white/5 rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300 text-sm font-semibold flex items-center gap-2"><User className="w-4 h-4" /> Cliente</Label>
                <Button type="button" size="sm" variant="ghost" onClick={() => setShowNovoCliente(!showNovoCliente)} className="text-emerald-300 hover:text-white hover:bg-emerald-500/10 h-7" data-testid="financas-venda-novo-cliente-toggle">
                  {showNovoCliente ? <X className="w-3.5 h-3.5 mr-1" /> : <UserPlus className="w-3.5 h-3.5 mr-1" />}
                  {showNovoCliente ? 'Cancelar' : 'Cadastro rápido'}
                </Button>
              </div>
              {!showNovoCliente ? (
                <>
                  {form.cliente_id ? (
                    // Cliente já selecionado — mostrar chip
                    (() => {
                      const c = clientes.find(x => x.id === form.cliente_id);
                      return (
                        <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-white text-sm font-medium truncate">{c?.nome || form.cliente_nome}</p>
                              {c && (c.cpf_cnpj || c.telefone) && (
                                <p className="text-xs text-emerald-300/70 truncate">
                                  {c.cpf_cnpj || ''}{c.cpf_cnpj && c.telefone ? ' · ' : ''}{c.telefone || ''}
                                </p>
                              )}
                            </div>
                          </div>
                          <Button type="button" size="sm" variant="ghost" onClick={() => { setForm({ ...form, cliente_id: '', cliente_nome: '' }); setBuscaCliente(''); }} className="text-slate-400 hover:text-white h-7 w-7 p-0">
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      );
                    })()
                  ) : (
                    // Busca + dropdown
                    <div className="relative">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          data-testid="financas-venda-cliente-busca"
                          placeholder="Buscar cliente por nome, CPF/CNPJ ou telefone..."
                          value={buscaCliente}
                          onChange={(e) => { setBuscaCliente(e.target.value); setShowDropdown(true); }}
                          onFocus={() => setShowDropdown(true)}
                          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                          className="bg-slate-900 border-white/10 text-white pl-10"
                        />
                      </div>
                      {showDropdown && (
                        <div className="absolute z-20 mt-1 w-full bg-slate-900 border border-white/10 rounded-lg shadow-xl max-h-72 overflow-y-auto">
                          {(() => {
                            const b = buscaCliente.toLowerCase();
                            const result = b
                              ? clientes.filter(c =>
                                  (c.nome || '').toLowerCase().includes(b) ||
                                  (c.nome_fantasia || '').toLowerCase().includes(b) ||
                                  (c.cpf_cnpj || '').toLowerCase().includes(b) ||
                                  (c.telefone || '').includes(buscaCliente)
                                )
                              : clientes.slice(0, 20);
                            if (result.length === 0) {
                              return <div className="p-3 text-sm text-slate-500 text-center">Nenhum cliente. Use "Cadastro rápido" ou cadastre em Clientes.</div>;
                            }
                            return result.map(c => (
                              <button
                                key={c.id}
                                type="button"
                                onMouseDown={() => { setForm({ ...form, cliente_id: c.id, cliente_nome: '' }); setBuscaCliente(''); setShowDropdown(false); }}
                                className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-b-0"
                              >
                                <p className="text-white text-sm font-medium truncate">{c.nome}</p>
                                <p className="text-xs text-slate-500 truncate">
                                  {c.cpf_cnpj || 'sem doc'}{c.telefone ? ` · ${c.telefone}` : ''}{c.cidade ? ` · ${c.cidade}/${c.uf || ''}` : ''}
                                </p>
                              </button>
                            ));
                          })()}
                        </div>
                      )}
                      <p className="text-xs text-slate-500 mt-2">Ou digite o nome de um cliente avulso abaixo:</p>
                      <Input placeholder="Cliente avulso (sem cadastro)" value={form.cliente_nome} onChange={(e) => setForm({ ...form, cliente_nome: e.target.value })} className="bg-slate-900 border-white/10 text-white mt-1" data-testid="financas-venda-cliente-avulso" />
                    </div>
                  )}
                </>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input data-testid="financas-venda-novo-cliente-nome" placeholder="Nome do cliente" value={novoClienteNome} onChange={(e) => setNovoClienteNome(e.target.value)} className="bg-slate-900 border-white/10 text-white" />
                  <div className="flex gap-2">
                    <Input placeholder="Telefone (opcional)" value={novoClienteFone} onChange={(e) => setNovoClienteFone(e.target.value)} className="bg-slate-900 border-white/10 text-white" />
                    <Button type="button" onClick={criarClienteRapido} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="financas-venda-criar-cliente">Criar</Button>
                  </div>
                </div>
              )}
            </div>

            {/* Itens */}
            <div className="bg-slate-950 border border-white/5 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-slate-300 text-sm font-semibold">Itens da venda</Label>
                <Button type="button" size="sm" variant="ghost" onClick={addItem} className="text-emerald-300 hover:bg-emerald-500/10 h-7" data-testid="financas-venda-add-item">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar item
                </Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={it._key || `item-${i}`} className="bg-slate-900 border border-white/5 rounded-lg p-2">
                    {/* Mobile: empilhar */}
                    <div className="md:hidden space-y-2">
                      <Select value={it.tipo} onValueChange={(v) => updateItem(i, { tipo: v })}>
                        <SelectTrigger className="bg-slate-950 border-white/10 text-white h-9"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-slate-900 border-white/10 text-white">
                          <SelectItem value="servico"><span className="flex items-center gap-1"><Wrench className="w-3 h-3" />Serviço</span></SelectItem>
                          <SelectItem value="mercadoria"><span className="flex items-center gap-1"><Package className="w-3 h-3" />Mercadoria</span></SelectItem>
                        </SelectContent>
                      </Select>
                      <Input placeholder="Descrição" value={it.descricao} onChange={(e) => updateItem(i, { descricao: e.target.value })} className="bg-slate-950 border-white/10 text-white h-9" />
                      <div className="grid grid-cols-3 gap-2">
                        <Input type="number" min="0" step="0.01" placeholder="Qtd" value={it.quantidade} onChange={(e) => updateItem(i, { quantidade: e.target.value })} className="bg-slate-950 border-white/10 text-white h-9" />
                        <Input type="number" min="0" step="0.01" placeholder="Valor un." value={it.valor_unitario} onChange={(e) => updateItem(i, { valor_unitario: e.target.value })} className="bg-slate-950 border-white/10 text-white h-9" />
                        <div className="flex items-center gap-1">
                          <span className="flex-1 text-right text-sm text-emerald-400 font-semibold">{fmtBRL(it.valor_total)}</span>
                          <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(i)} className="text-red-500 hover:text-red-400 h-9 px-2"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </div>
                    {/* Desktop: linha */}
                    <div className="hidden md:grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-2">
                        <Select value={it.tipo} onValueChange={(v) => updateItem(i, { tipo: v })}>
                          <SelectTrigger className="bg-slate-950 border-white/10 text-white h-9"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-slate-900 border-white/10 text-white">
                            <SelectItem value="servico"><span className="flex items-center gap-1"><Wrench className="w-3 h-3" />Serviço</span></SelectItem>
                            <SelectItem value="mercadoria"><span className="flex items-center gap-1"><Package className="w-3 h-3" />Mercadoria</span></SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Input placeholder="Descrição" value={it.descricao} onChange={(e) => updateItem(i, { descricao: e.target.value })} className="col-span-5 bg-slate-950 border-white/10 text-white h-9" />
                      <Input type="number" min="0" step="0.01" placeholder="Qtd" value={it.quantidade} onChange={(e) => updateItem(i, { quantidade: e.target.value })} className="col-span-1 bg-slate-950 border-white/10 text-white h-9" />
                      <Input type="number" min="0" step="0.01" placeholder="Valor un." value={it.valor_unitario} onChange={(e) => updateItem(i, { valor_unitario: e.target.value })} className="col-span-2 bg-slate-950 border-white/10 text-white h-9" />
                      <div className="col-span-1 text-right text-sm text-emerald-400 font-semibold">{fmtBRL(it.valor_total)}</div>
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(i)} className="col-span-1 text-red-500 hover:text-red-400 h-9 px-2"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totais */}
            <div className="bg-slate-950 border border-white/5 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Subtotal</span>
                <span className="text-white">{fmtBRL(subtotal)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-400">Desconto</span>
                <Input type="number" min="0" step="0.01" value={form.desconto} onChange={(e) => setForm({ ...form, desconto: e.target.value })} className="bg-slate-900 border-white/10 text-white h-8 w-32 text-right" />
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-white/10">
                <span className="text-white font-semibold">Total</span>
                <span className="text-2xl font-bold text-emerald-400">{fmtBRL(totalForm)}</span>
              </div>
            </div>

            {/* Pagamento + status + datas */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 text-xs">Forma de Pagamento</Label>
                <Select value={form.forma_pagamento} onValueChange={(v) => setForm({ ...form, forma_pagamento: v })}>
                  <SelectTrigger className="bg-slate-950 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10 text-white">
                    {FORMAS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-slate-950 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10 text-white">
                    <SelectItem value="pago">Pago</SelectItem>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="atrasado">Atrasado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Data</Label>
                <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} className="bg-slate-950 border-white/10 text-white" required />
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Vencimento (se pendente)</Label>
                <Input type="date" value={form.data_vencimento} onChange={(e) => setForm({ ...form, data_vencimento: e.target.value })} className="bg-slate-950 border-white/10 text-white" />
              </div>
            </div>

            <div>
              <Label className="text-slate-400 text-xs">Observação</Label>
              <Textarea value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} className="bg-slate-950 border-white/10 text-white" rows={2} />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">Cancelar</Button>
              <Button type="submit" data-testid="financas-venda-salvar" className="bg-emerald-600 hover:bg-emerald-500 text-white">{editing ? 'Atualizar' : 'Registrar Venda'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
